import { memo, useState } from "react";
import type { FC, ReactNode } from "react";
import {
    IconCandler,
    IconSetting,
    IconStarFilled,
    IconStarOutline,
} from "~/components/Iconfont/Iconfont";
import TokenSelect from "~/components/TokenSelect";
import { formatPrice, formatPercent } from "~/utils/number-format";
import { useNavigate } from "react-router";
import { Button, Skeleton } from "antd-mobile";
import clsx from "clsx";
import { TradeToken } from "~/types";
import { postAddFavoriteToken, postRemoveFavoriteToken } from "~/api/endpoints";
import { useSnackbar } from "~/Provider";
import { hapticFeedback } from "@telegram-apps/sdk-react";

interface IProps {
    children?: ReactNode;
    token: TradeToken;
    loading: boolean;
    isFavorite: boolean;
    setIsFavorite: (isFavorite: boolean) => void;
    updateFavoriteCache: (isFavorite: boolean) => void;
}

const TradeHeader: FC<IProps> = ({
    loading,
    token,
    isFavorite,
    setIsFavorite,
    updateFavoriteCache,
}) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (!token) return;
        const chain_id = String(token.token_base?.chain_id);
        const contract = String(token.token_base?.contract);
        navigate(`/k-detail?chain_id=${chain_id}&contract=${contract}`);
    };

    const handleSettingClick = () => {
        navigate("/trade-settings");
    };

    const snackbar = useSnackbar();
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    const handleFavoriteClick = () => {
        if (favoriteLoading) return;
        setFavoriteLoading(true);
        hapticFeedback.impactOccurred("light");

        const chain_id = String(token?.token_base?.chain_id);
        const contract = String(token?.token_base?.contract);
        if (isFavorite) {
            setIsFavorite(false);
            postRemoveFavoriteToken(chain_id, contract)
                .then(() => {
                    updateFavoriteCache(false);
                })
                .catch((err) => {
                    snackbar.error(
                        "Operation Failed, Please try again later!",
                        err
                    );
                    setIsFavorite(true);
                })
                .finally(() => setFavoriteLoading(false));
        } else {
            setIsFavorite(true);
            postAddFavoriteToken(chain_id, contract)
                .then(() => {
                    updateFavoriteCache(true);
                })
                .catch((err) => {
                    snackbar.error(
                        "Operation Failed, Please try again later!",
                        err
                    );
                    setIsFavorite(false);
                })
                .finally(() => setFavoriteLoading(false));
        }
    };

    if (loading || !token?.token_base?.symbol) {
        return (
            <div className="py-[12px] px-[16px] ">
                <div className="flex items-center">
                    <Skeleton animated className={"w-32 h-12 rounded-[4px]"} />
                </div>
                <div className="flex mt-[8px]">
                    <div className="text-h3 text-text_1 font-semibold">
                        <Skeleton
                            animated
                            className={"w-24 h-6 rounded-[4px]"}
                        />
                    </div>
                    <div className="text-text_buy text-body4 ml-[4px]"></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="pt-3 pb-1 px-4 sticky -top-[0.5px] left-0 w-full z-20 bg-bg_0">
                <div className="flex items-center justify-between">
                    <TokenSelect
                        symbol={String(token.token_base?.symbol).toUpperCase()}
                        logo={String(token.token_base?.logo)}
                        chainId={String(token.token_base?.chain_id)}
                        chainName={String(token.token_base?.chain_name)}
                        chainLogo={String(token.token_base?.chain_logo)}
                        contract={token.token_base?.contract}
                    />
                    <div className="flex">
                        <Button
                            fill="none"
                            style={{ padding: "7px 8px" }}
                            onClick={handleFavoriteClick}
                        >
                            {isFavorite ? (
                                <IconStarFilled className="text-text_2 text-h5" />
                            ) : (
                                <IconStarOutline className="text-text_2 text-h5" />
                            )}
                        </Button>
                        <Button
                            fill="none"
                            style={{ padding: "7px 8px" }}
                            onClick={handleClick}
                        >
                            <IconCandler className="text-text_2 text-h5" />
                        </Button>
                        <Button
                            fill="none"
                            style={{ padding: "7px 8px" }}
                            onClick={handleSettingClick}
                        >
                            <IconSetting className="text-text_2 text-h5" />
                        </Button>
                    </div>
                </div>
            </div>
            <div className="flex pb-3 px-4">
                <div className="text-h3 text-text_1 font-semibold">
                    {formatPrice(String(token.price))}
                </div>
                <div
                    className={clsx(
                        "text-body4 ml-[4px] mt-[3px]",
                        Number(token.change) == 0
                            ? "!text-text_3"
                            : Number(token.change) >= 0
                              ? "!text-text_buy"
                              : "!text-text_sell"
                    )}
                >
                    {formatPercent(
                        Number.isNaN(Number(token.change))
                            ? 0
                            : Number(token.change)
                    )}
                </div>
            </div>
        </>
    );
};

export default memo(TradeHeader);
